import type { Locale } from "../../shared/dto";
import { intlLanguage } from "../date-format";
import { DETAIL_CONFIG_METADATA, hasImportDetail, importDetailCodes } from "../../shared/stripe-import-detail";
import { profileResearchDiagnosticKey } from "../profile-research-diagnostics";

// Servis wave 3 shared vocabulary: the import row shape the console consumes,
// the reason and next-step lookups, and the one feedback strip that gives every
// action in this area its five visible states (ready, working, done, error,
// safe repeat). Components live in AdminImportsCreate/AdminImportsHistory so
// this module can be imported by both without a cycle.

export type ImportSource = "stripe" | "dossier" | "research";
export type ImportStatus = "done" | "failed" | "pending" | "skipped";
export type ActionState = "idle" | "working" | "done" | "error";

export interface ImportRow {
  id: string;
  source: ImportSource;
  rowId: string;
  occurredAt: string;
  subject: string;
  subjectEmail: string | null;
  clientId: string | null;
  clientName: string | null;
  status: ImportStatus;
  statusKey: string;
  detail: string | null;
  note: string | null;
  attempts: number | null;
  retryKind: "research" | null;
  isTest: boolean;
}

export interface ImportHistoryResponse {
  ok: boolean;
  imports: ImportRow[];
  summary: { total: number; failed: number; pending: number };
}

export type AutomationState = "done" | "working" | "waiting" | "failed";

export interface ClientCreationSummary {
  ok: boolean;
  client: { id: string; name: string; email: string; marketLanguage: Locale; includesApp: boolean; createdAt: string };
  owner: { name: string | null; email: string };
  partner: { name: string | null; email: string } | null;
  automations: Array<{ key: string; state: AutomationState; stateKey: string }>;
  deepLink: string;
}

export const IMPORT_SOURCES: ImportSource[] = ["stripe", "dossier", "research"];
export const IMPORT_STATUSES: ImportStatus[] = ["failed", "pending", "done", "skipped"];

export function visibleImportRows(rows: ImportRow[], hideTestTenants: boolean): ImportRow[] {
  return hideTestTenants ? rows.filter((row) => !row.isTest) : rows;
}

/**
 * The counts above the list, taken from the SAME rows the list draws from.
 * The server census counts the whole table, and with the global test filter on
 * that read "Selhalo: 14" above eighteen rows without one failure among them:
 * all fourteen belonged to test tenants the filter removes, so the number named
 * records nobody could reach (audit S-02). Pass the test-filtered rows here,
 * before the local source and status filters, so the header describes what this
 * panel can show and the selects narrow it visibly.
 */
export function importCounts(rows: ImportRow[]): { total: number; failed: number; pending: number } {
  return {
    total: rows.length,
    failed: rows.filter((row) => row.status === "failed").length,
    pending: rows.filter((row) => row.status === "pending").length
  };
}

export function filteredImportRows(
  rows: ImportRow[],
  filters: { source: ImportSource | "all"; status: ImportStatus | "all" }
): ImportRow[] {
  return rows
    .filter((row) => filters.source === "all" || row.source === filters.source)
    .filter((row) => filters.status === "all" || row.status === filters.status);
}

/**
 * Why this row stopped, in the operator's words - EVERY reason it has, because
 * since F148 a Stripe delivery's `detail` is a set of codes and not one. The
 * database keeps internal codes (`missing_default_partner`, `stripe_api_402`,
 * `metadata_language`); the console never shows them raw, exactly as the Stripe
 * panel already does for its own events.
 */
export function importReasonKeys(row: ImportRow): string[] {
  const diagnostic = row.source !== "stripe" ? profileResearchDiagnosticKey(row.detail) : null;
  if (diagnostic) return [diagnostic];
  const suffixes = row.source === "stripe"
    ? stripeReasons(row)
    : [row.source === "dossier" ? dossierReason(row) : researchReason(row)];
  return suffixes.filter((suffix): suffix is string => Boolean(suffix))
    .map((suffix) => `staffConsole.adminImports.reason.${suffix}`);
}

/**
 * F148: which channel configured this purchase, as a plain fact beside the
 * client's e-mail - never as a "reason", because nothing went wrong. Only a
 * Stripe row that really was configured has one to state.
 */
export function importConfigSourceKey(row: ImportRow): string | null {
  if (row.source !== "stripe") return null;
  if (row.statusKey !== "imported" && row.statusKey !== "switch_off") return null;
  return hasImportDetail(row.detail, DETAIL_CONFIG_METADATA)
    ? "staffConsole.adminImports.configSource.metadata"
    : "staffConsole.adminImports.configSource.mapping";
}

/** The concrete next step, only where one exists. */
export function importFixKey(row: ImportRow): string | null {
  if (row.source === "research" && row.status === "failed"
    && errorReason(row.detail) === "contextTooLarge") return "staffConsole.adminImports.fix.reduceContext";
  // The server only offers a repeat on a still-running row once it has stalled,
  // so a repeat button here means the row is stuck, and the line says so.
  if (row.status === "pending" && row.retryKind) return "staffConsole.adminImports.fix.stalledImport";
  if (row.source !== "stripe") return null;
  const codes = importDetailCodes(row.detail);
  // The outcome comes first where it is the BLOCKER: a dormant switch stops
  // every purchase, so "turn it on" is the next step even on a row that also
  // carries a broken sibling product (its sentences still print above).
  if (row.statusKey === "no_match") return "staffConsole.adminImports.fix.addMapping";
  if (row.statusKey === "switch_off") return "staffConsole.adminImports.fix.enableSwitch";
  // F148 next: a product that declared itself to Launchpad and cannot be read
  // is fixed at the PRODUCT, and telling the admin to add a mapping row instead
  // would send them to build the very table this wave is retiring.
  if (codes.some((code) => code.startsWith("metadata_"))) return "staffConsole.adminImports.fix.fixMetadata";
  if (codes.includes("missing_default_partner")) return "staffConsole.adminImports.fix.setPartner";
  if (codes.some((code) => code.startsWith("stripe_api_"))) return "staffConsole.adminImports.fix.checkStripe";
  return null;
}

export function importStatusKey(status: ImportStatus): string {
  return `staffConsole.adminImports.status.${status}`;
}

export function importSourceKey(source: ImportSource): string {
  return `staffConsole.adminImports.source.${source}`;
}

/**
 * Every reason a Stripe row carries, in the order the server wrote them.
 *
 * The outcome speaks first where it is the whole story (an unmatched product,
 * a dormant switch), because those rows have no detail to add. Everything else
 * reads the code SET: F148 made `detail` several facts, so a metadata purchase
 * whose default partner setting is also stale states both instead of dropping
 * whichever one lost a precedence chain.
 */
function stripeReasons(row: ImportRow): (string | null)[] {
  if (row.statusKey === "already_client") return ["alreadyClient"];
  if (row.statusKey === "deferred_unpaid") return ["unpaid"];
  const codes = importDetailCodes(row.detail);
  const reasons: (string | null)[] = [];
  if (row.statusKey === "no_match") reasons.push("noMatch");
  if (row.statusKey === "switch_off") reasons.push("switchOff");
  for (const code of codes) {
    const reason = stripeDetailReason(code);
    if (reason && !reasons.includes(reason)) reasons.push(reason);
  }
  if (reasons.length === 0 && row.status === "failed") reasons.push("unknown");
  return reasons;
}

function stripeDetailReason(code: string): string | null {
  // F148: one sentence per field, so an operator who mistyped
  // `launchpad_language` reads WHICH key is wrong instead of "import selhal".
  if (code === "metadata_flag") return "metadataFlag";
  if (code === "metadata_label") return "metadataLabel";
  if (code === "metadata_language") return "metadataLanguage";
  if (code === "metadata_app") return "metadataApp";
  if (code === "metadata_source") return "metadataSource";
  if (code === "metadata_start") return "metadataStart";
  if (code === "metadata_payments") return "metadataPayments";
  // F153, the second dialect. Same law, its own keys: an operator who mistyped
  // `productType` reads which key is wrong, and a product carrying BOTH sets
  // that disagree reads that instead of a nameless refusal.
  if (code === "metadata_product_type") return "metadataProductType";
  if (code === "metadata_product_language") return "metadataProductLanguage";
  if (code === "metadata_product_app") return "metadataProductApp";
  if (code === "metadata_dialects_disagree") return "metadataDialectsDisagree";
  if (code === "missing_default_partner") return "missingPartner";
  // 🔴 F139 writes this on a SUCCESSFUL import ("a dead setting is a thing the
  // admin has to see") and no surface has ever rendered it: here it fell
  // through to null, and in the Stripe panel to the generic "import se
  // nepodařilo" - a failure sentence printed over a client that was created.
  // Found by F148, because the code set is what made both readers look at it.
  if (code === "default_partner_unavailable") return "stalePartner";
  // F171: the "Jen prodej" partner setting went stale; the client still exists.
  if (code === "sales_only_partner_unavailable") return "staleSalesOnlyPartner";
  if (code === "missing_email") return "missingEmail";
  if (code === "no_products_in_session") return "noProducts";
  if (code === "research_job_skipped") return "profileAlreadyQueued";
  if (code === "session_replay") return "repeatedPurchase";
  if (code.startsWith("stripe_api_")) return "stripeConnection";
  if (code.startsWith("create_")) return "clientCreation";
  // The configuration channel is a FACT, not a reason: it has its own line
  // (`importConfigSourceKey`) and must not read as something that went wrong.
  return null;
}

function dossierReason(row: ImportRow): string | null {
  if (row.statusKey === "importing") return row.retryKind ? "dossierStalled" : "dossierImporting";
  if (row.statusKey === "superseded") return "dossierSuperseded";
  if (row.statusKey !== "failed") return null;
  return errorReason(row.detail) ?? "dossierFailed";
}

function researchReason(row: ImportRow): string | null {
  if (row.statusKey === "canceled") return "researchCanceled";
  if (row.statusKey !== "failed") return null;
  return errorReason(row.detail) ?? "unknown";
}

function errorReason(detail: string | null): string | null {
  if (!detail) return null;
  const normalized = detail.toLowerCase();
  if (normalized.includes("prompt is too long")) return "contextTooLarge";
  if (normalized.includes("fact")) return "notEnoughFacts";
  if (normalized.includes("timeout") || normalized.includes("network")) return "temporary";
  return null;
}

/**
 * The action feedback law: an action must show that it is working, name the
 * result, and offer a safe repeat when it fails. A bare HTTP 200 is not a
 * confirmation, so the done branch always carries a message key naming what
 * happened.
 */
export function AdminImportsFeedback(props: {
  state: ActionState;
  message: string | null;
  error?: string;
  working?: string;
  t: (key: string) => string;
}) {
  return (
    <div className="admin-imports-feedback" aria-live="polite">
      {props.state === "working" && <p>{props.working ?? props.t("staffConsole.adminImports.working")}</p>}
      {props.state === "done" && props.message && <p className="success-message">{props.message}</p>}
      {/* A failure carries its own sentence when the caller has one (S2-01:
          the lost connection), otherwise the panel's default. */}
      {props.state === "error" && (
        <p className="error-message" role="alert">
          {props.message ?? props.error ?? props.t("staffConsole.adminImports.actionFailed")}
        </p>
      )}
    </div>
  );
}

export function formatImportTime(value: string, locale: Locale): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat(intlLanguage(locale), { dateStyle: "short", timeStyle: "short" }).format(date)
    : value;
}

export function fillCount(template: string, count: number): string {
  return template.replace("{count}", String(count));
}

export function fillClient(template: string, client: string): string {
  return template.replace("{client}", client);
}
